import { Link } from 'react-router-dom';
import { Star, GitFork } from 'lucide-react';
import clsx from 'clsx';
import type { Template } from '../types/wizard';

const categoryColors: Record<string, string> = {
  Frontend: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  Backend: 'bg-green-500/10 text-green-400 border-green-500/20',
  'Full Stack': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  'Data/ML': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  DevOps: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  Mobile: 'bg-pink-500/10 text-pink-400 border-pink-500/20',
  API: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
};

interface TemplateCardProps {
  template: Template;
}

export default function TemplateCard({ template }: TemplateCardProps) {
  const colorClass =
    categoryColors[template.category] ||
    'bg-gray-500/10 text-gray-400 border-gray-500/20';

  return (
    <div className="card card-hover group flex flex-col h-full">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <span className={clsx('badge border', colorClass)}>
          {template.category}
        </span>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Star className="h-3 w-3" />
            {template.stars}
          </span>
          <span className="flex items-center gap-1">
            <GitFork className="h-3 w-3" />
            {template.forks}
          </span>
        </div>
      </div>

      {/* Name */}
      <h3 className="text-white font-semibold mb-2 group-hover:text-accent transition-colors">
        {template.name}
      </h3>

      {/* Description */}
      <p className="text-sm text-gray-500 mb-4 flex-1 line-clamp-3">
        {template.description}
      </p>

      {/* Tech Stack */}
      <div className="flex flex-wrap gap-1.5 mb-5">
        {template.techStack.slice(0, 5).map((tech) => (
          <span
            key={tech}
            className="px-2 py-0.5 text-xs rounded bg-surface-100 text-gray-400 border border-dark-border"
          >
            {tech}
          </span>
        ))}
        {template.techStack.length > 5 && (
          <span className="px-2 py-0.5 text-xs rounded bg-surface-100 text-gray-500 border border-dark-border">
            +{template.techStack.length - 5}
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-4 border-t border-dark-border">
        <Link
          to={`/wizard?template=${template.id}`}
          className="btn-primary flex-1 text-sm py-2"
        >
          Use Template
        </Link>
        <Link
          to={`/templates/${template.id}`}
          className="btn-ghost flex-1 text-sm py-2"
        >
          View
        </Link>
      </div>
    </div>
  );
}
